import { useMemo, useRef, useState } from "react";
import { Form, Link, useRouteLoaderData } from "react-router-dom";

import { AsyncFormSelect, SelectOption } from "src/components/common/AsyncSelect";
import { handleFilesInput, paymentMethodList, round } from "src/components/common/functions";
import { myOptions } from "src/components/users/UserForm";
import { useAuthStore, User } from "../main/authStore";

import { defaultPurchase, defaultPurchaseLineItem, Purchase } from "./types";

type FormPurchase = Omit<Partial<Purchase>, "requester" | "approver" | "data"> & {
    requester?: SelectOption | null;
    approver?: SelectOption | null;
    project?: SelectOption | null;
    data: Purchase["data"];
};

export default function PurchaseForm({
    keyword,
}: {
    keyword: "Add" | "Edit";
}) {
    const currentUser = useAuthStore(state => state.user) as User;
    const existing = useRouteLoaderData("Purchase") as { purchase?: Purchase } | undefined;
    const quoteFileInputRef = useRef<HTMLInputElement>(null);
    const receiptFileInputRef = useRef<HTMLInputElement>(null);
    const [purchase, setPurchase] = useState<FormPurchase>(
        existing?.purchase
            ? {
                ...existing.purchase,
                requester: existing.purchase.requester ?? {
                    value: existing.purchase.requesterId,
                    label: existing.purchase.requesterName,
                },
                approver: existing.purchase.approver ?? {
                    value: existing.purchase.approverId,
                    label: existing.purchase.approverName,
                },
                project: existing.purchase.projectId
                    ? { value: String(existing.purchase.projectId), label: existing.purchase.projectName }
                    : null,
                data: existing.purchase.data?.length ? existing.purchase.data : [{ ...defaultPurchaseLineItem }],
            }
            : {
                ...defaultPurchase,
                requester: {
                    value: currentUser.id,
                    label: currentUser.userName,
                },
                project: null,
                approver: null,
                department: currentUser.department,
                data: [{ ...defaultPurchaseLineItem }],
            }
    );

    const subtotal = useMemo(
        () =>
            purchase.data.reduce((sum, line) => {
                return sum + parseAmount(line.lineTotal);
            }, 0),
        [purchase.data]
    );
    const tax = parseAmount(purchase.tax);
    const discount = parseAmount(purchase.discount);
    const total = round(subtotal + tax - discount, 2);

    const setLine = (index: number, next: Purchase["data"][number]) => {
        setPurchase(prev => ({
            ...prev,
            data: prev.data.map((line, lineIndex) => (lineIndex === index ? next : line)),
        }));
    };

    const clearFile = (field: "quote" | "receipt") => {
        if (field === "quote") {
            if (quoteFileInputRef.current) quoteFileInputRef.current.value = "";
            setPurchase(prev => ({ ...prev, quoteFileId: "", quoteUrl: "" }));
            return;
        }
        if (receiptFileInputRef.current) receiptFileInputRef.current.value = "";
        setPurchase(prev => ({ ...prev, receiptFileId: "", receiptUrl: "" }));
    };

    return (
        <Form method = "post" encType = "multipart/form-data" className = "my-3">
            <input type = "hidden" name = "action" value = {keyword} />
            <input type = "hidden" name = "id" value = {purchase.id ?? ""} />
            <input type = "hidden" name = "data" value = {JSON.stringify(purchase.data)} />
            <input type = "hidden" name = "subtotal" value = {subtotal.toFixed(2)} />
            <input type = "hidden" name = "total" value = {total.toFixed(2)} />
            <input type = "hidden" name = "quoteFileIdCurrent" value = {purchase.quoteFileId ?? ""} />
            <input type = "hidden" name = "receiptFileIdCurrent" value = {purchase.receiptFileId ?? ""} />
            <div className = "cssSeperateWhenWide ps-3 mb-3">
                <div className = "flex-grow-1 me-3">
                    <div className = "d-flex align-items-center mt-2">
                        <label className = "fw-bold text-nowrap mb-0" htmlFor = "poNumber">PO Number:</label>
                        <input
                            required
                            className = "form-control flex-grow-1 ms-3"
                            id = "poNumber"
                            name = "poNumber"
                            value = {purchase.poNumber ?? ""}
                            onChange = {e => setPurchase({ ...purchase, poNumber: e.currentTarget.value.replace(/\s+/g, "").replace(/\//g, "") })}
                        />
                    </div>
                    <div className = "d-flex align-items-center mt-2">
                        <label className = "fw-bold text-nowrap mb-0" htmlFor = "poDate">PO Date:</label>
                        <input
                            required
                            type = "date"
                            min = "1900-01-01"
                            max = "2999-12-31"
                            className = "form-control flex-grow-1 ms-3"
                            id = "poDate"
                            name = "poDate"
                            value = {purchase.poDate ?? ""}
                            onChange = {e => setPurchase({ ...purchase, poDate: e.currentTarget.value })}
                        />
                    </div>
                    <div className = "d-flex align-items-center mt-2">
                        <label className = "fw-bold text-nowrap mb-0" htmlFor = "poType">Form Type:</label>
                        <select
                            required
                            className = "form-control flex-grow-1 ms-3"
                            id = "poType"
                            name = "poType"
                            value = {purchase.poType ?? "pr"}
                            onChange = {e => setPurchase({ ...purchase, poType: e.currentTarget.value as "pr" | "re" })}
                        >
                            <option value = "pr">Purchase Request</option>
                            <option value = "re">Reimbursement</option>
                        </select>
                    </div>
                    <div className = "d-flex align-items-center mt-2">
                        <label className = "fw-bold text-nowrap mb-0" htmlFor = "category">Category:</label>
                        <select
                            required
                            className = "form-control flex-grow-1 ms-3"
                            id = "category"
                            name = "category"
                            value = {purchase.category ?? ""}
                            onChange = {e => setPurchase({ ...purchase, category: e.currentTarget.value })}
                        >
                            <option value = "">Select</option>
                            {[
                                "Project Expense",
                                "Outside Service Expense",
                                "Office Supply",
                                "Vehicle Maintenance",
                                "Equipment Expense",
                                "Equipment Maintenance",
                                "Yard Material",
                                "Vehicle Rental",
                                "Traffic Control Related",
                                "Subscription",
                                "In-House Training",
                                "QA/QC",
                                "Marketing",
                                "Safety",
                                "Accommodation",
                                "Saw Cutting",
                                "Scanning & Coring Materials",
                            ].map(value => (
                                <option key = {value} value = {value}>{value}</option>
                            ))}
                        </select>
                    </div>
                    <div className = "d-flex align-items-center mt-2">
                        <label className = "fw-bold text-nowrap mb-0" htmlFor = "department">Department:</label>
                        <select
                            required
                            className = "form-control flex-grow-1 ms-3"
                            id = "department"
                            name = "department"
                            value = {purchase.department ?? ""}
                            onChange = {e => setPurchase({ ...purchase, department: e.currentTarget.value })}
                        >
                            <option value = "">Select</option>
                            {Object.entries(myOptions.department).map(([group, values]) => (
                                <optgroup key = {group} label = {group}>
                                    {(values as string[]).map(value => <option key = {value} value = {value}>{value}</option>)}
                                </optgroup>
                            ))}
                        </select>
                    </div>
                    <div className = "d-flex align-items-center mt-2">
                        <label className = "fw-bold text-nowrap mb-0" htmlFor = "paymentMethod">Payment Method:</label>
                        <select
                            required
                            className = "form-control flex-grow-1 ms-3"
                            id = "paymentMethod"
                            name = "paymentMethod"
                            value = {purchase.paymentMethod ?? ""}
                            onChange = {e => setPurchase({ ...purchase, paymentMethod: e.currentTarget.value })}
                        >
                            <option value = "">Select</option>
                            {paymentMethodList.map(value => <option key = {value} value = {value}>{value}</option>)}
                        </select>
                    </div>
                    {purchase.paymentMethod === "Pay by American Express card" ? (
                        <div className = "d-flex align-items-center mt-2">
                            <label className = "fw-bold text-nowrap mb-0" htmlFor = "last4">Last 4:</label>
                            <input
                                required
                                pattern = "[0-9]{4}"
                                maxLength = {4}
                                className = "form-control flex-grow-1 ms-3"
                                id = "last4"
                                name = "last4"
                                value = {purchase.last4 ?? ""}
                                onChange = {e => setPurchase({ ...purchase, last4: e.currentTarget.value.replace(/\D/g, "").slice(0, 4) })}
                            />
                        </div>
                    ) : null}
                </div>
                <div className = "flex-grow-1 me-3">
                    <div className = "d-flex align-items-center mt-2">
                        <label className = "fw-bold text-nowrap mb-0" htmlFor = "requesterId">Requester:</label>
                        <div className = "flex-grow-1 ms-3">
                            {currentUser.purchases === "edit" ? (
                                <AsyncFormSelect
                                    required
                                    inputId = "requesterId"
                                    name = "requesterId"
                                    fileName = "common/searchUser.php"
                                    placeholder = "Search user…"
                                    value = {purchase.requester ?? null}
                                    onChange = {next => setPurchase({ ...purchase, requester: next as SelectOption | null })}
                                />
                            ) : (
                                <>
                                    <span>{purchase.requester?.label ?? currentUser.userName}</span>
                                    <input type = "hidden" name = "requesterId" value = {purchase.requester?.value ?? currentUser.id} />
                                </>
                            )}
                        </div>
                    </div>
                    <div className = "d-flex align-items-center mt-2">
                        <label className = "fw-bold text-nowrap mb-0" htmlFor = "projectId">Project:</label>
                        <div className = "flex-grow-1 ms-3">
                            <AsyncFormSelect
                                required = {purchase.category === "Project Expense"}
                                inputId = "projectId"
                                name = "projectId"
                                fileName = "common/searchProject.php"
                                placeholder = "Search project…"
                                value = {purchase.project ?? null}
                                onChange = {next => setPurchase({ ...purchase, project: next as SelectOption | null })}
                            />
                        </div>
                    </div>
                    <div className = "d-flex align-items-center mt-2">
                        <label className = "fw-bold text-nowrap mb-0" htmlFor = "approverId">Approver:</label>
                        <div className = "flex-grow-1 ms-3">
                            <AsyncFormSelect
                                required
                                inputId = "approverId"
                                name = "approverId"
                                fileName = "common/searchUser.php"
                                placeholder = "Search user…"
                                value = {purchase.approver ?? null}
                                onChange = {next => setPurchase({ ...purchase, approver: next as SelectOption | null })}
                            />
                        </div>
                    </div>
                    <div className = "d-flex align-items-center mt-2">
                        <label className = "fw-bold text-nowrap mb-0" htmlFor = "billable">Billable:</label>
                        <select
                            required
                            className = "form-control flex-grow-1 ms-3"
                            id = "billable"
                            name = "billable"
                            value = {purchase.billable ?? "no"}
                            onChange = {e => setPurchase({ ...purchase, billable: e.currentTarget.value as "yes" | "no" })}
                        >
                            <option value = "yes">Yes</option>
                            <option value = "no">No</option>
                        </select>
                    </div>
                    <div className = "d-flex align-items-center mt-2">
                        <label className = "fw-bold text-nowrap mb-0" htmlFor = "includedInProposal">Included In Proposal:</label>
                        <select
                            required
                            className = "form-control flex-grow-1 ms-3"
                            id = "includedInProposal"
                            name = "includedInProposal"
                            value = {purchase.includedInProposal ?? "no"}
                            onChange = {e => setPurchase({ ...purchase, includedInProposal: e.currentTarget.value as "yes" | "no" })}
                        >
                            <option value = "yes">Yes</option>
                            <option value = "no">No</option>
                        </select>
                    </div>
                    <div className = "d-flex align-items-center mt-2">
                        <label className = "fw-bold text-nowrap mb-0" htmlFor = "clientInvoiceNumber">Client Invoice Number:</label>
                        <input
                            className = "form-control flex-grow-1 ms-3"
                            id = "clientInvoiceNumber"
                            name = "clientInvoiceNumber"
                            value = {purchase.clientInvoiceNumber ?? ""}
                            onChange = {e => setPurchase({ ...purchase, clientInvoiceNumber: e.currentTarget.value })}
                        />
                    </div>
                    <div className = "mt-3">
                        <label className = "fw-bold text-nowrap mb-0" htmlFor = "quoteFile">Quote PDF:</label>
                        <input
                            className = "form-control mt-2"
                            id = "quoteFile"
                            name = "quoteFile"
                            type = "file"
                            accept = "application/pdf,.pdf"
                            ref = {quoteFileInputRef}
                            onChange = {handleFilesInput}
                        />
                        {purchase.quoteUrl ? <a href = {purchase.quoteUrl} target = "_blank" rel = "noreferrer" className = "d-inline-block mt-1">Current quote</a> : null}
                        {purchase.quoteUrl ? <div className = "form-text">Upload a new PDF to replace the current quote file.</div> : null}
                        {purchase.quoteUrl ? (
                            <div>
                                <button
                                    type = "button"
                                    className = "btn btn-outline-danger btn-sm mt-2"
                                    onClick = {() => clearFile("quote")}
                                >
                                    Clear Quote File
                                </button>
                            </div>
                        ) : null}
                    </div>
                    <div className = "mt-3">
                        <label className = "fw-bold text-nowrap mb-0" htmlFor = "receiptFile">Receipt PDF:</label>
                        <input
                            className = "form-control mt-2"
                            id = "receiptFile"
                            name = "receiptFile"
                            type = "file"
                            accept = "application/pdf,.pdf"
                            ref = {receiptFileInputRef}
                            onChange = {handleFilesInput}
                        />
                        {purchase.receiptUrl ? <a href = {purchase.receiptUrl} target = "_blank" rel = "noreferrer" className = "d-inline-block mt-1">Current receipt</a> : null}
                        {purchase.receiptUrl ? <div className = "form-text">Upload a new PDF to replace the current receipt file.</div> : null}
                        {purchase.receiptUrl ? (
                            <div>
                                <button
                                    type = "button"
                                    className = "btn btn-outline-danger btn-sm mt-2"
                                    onClick = {() => clearFile("receipt")}
                                >
                                    Clear Receipt File
                                </button>
                            </div>
                        ) : null}
                    </div>
                </div>
            </div>
            <div className = "px-3 mb-3">
                <label className = "fw-bold mb-2" htmlFor = "notes">Notes:</label>
                <textarea
                    className = "form-control"
                    id = "notes"
                    name = "notes"
                    rows = {4}
                    value = {purchase.notes ?? ""}
                    onChange = {e => setPurchase({ ...purchase, notes: e.currentTarget.value })}
                />
            </div>
            <div className = "px-3 mb-3">
                <div className = "d-flex justify-content-between align-items-center mb-2">
                    <div className = "fw-bold">Line Items</div>
                    <button
                        type = "button"
                        className = "btn btn-outline-primary"
                        onClick = {() => setPurchase(prev => ({ ...prev, data: [...prev.data, { ...defaultPurchaseLineItem }] }))}
                    >
                        Add Line
                    </button>
                </div>
                <div className = "table-responsive">
                    <table className = "table">
                        <thead className = "noTableTopBorder">
                            <tr>
                                <th>Vendor</th>
                                <th>Description</th>
                                <th>Unit Price</th>
                                <th>Quantity</th>
                                <th>Line Total</th>
                                <th />
                            </tr>
                        </thead>
                        <tbody>
                            {purchase.data.map((line, index) => (
                                <tr key = {index}>
                                    <td style = {{ minWidth: 260 }}>
                                        <AsyncFormSelect
                                            inputId = {`vendor-${index}`}
                                            fileName = "common/searchVendor.php"
                                            placeholder = "Search vendor…"
                                            value = {line.vendorId ? { value: String(line.vendorId), label: line.vendorName } : null}
                                            onChange = {next => {
                                                const option = next as SelectOption | null;
                                                setLine(index, {
                                                    ...line,
                                                    vendorId: option?.value ?? "",
                                                    vendorName: option?.label ?? "",
                                                });
                                            }}
                                        />
                                    </td>
                                    <td>
                                        <input
                                            className = "form-control"
                                            value = {line.description}
                                            onChange = {e => setLine(index, { ...line, description: e.currentTarget.value })}
                                        />
                                    </td>
                                    <td>
                                        <input
                                            type = "number"
                                            min = "0"
                                            step = "0.01"
                                            className = "form-control"
                                            value = {line.unitPrice}
                                            onChange = {e => {
                                                const unitPrice = e.currentTarget.value;
                                                setLine(index, {
                                                    ...line,
                                                    unitPrice,
                                                    lineTotal: calcLineTotal(unitPrice, line.quantity),
                                                });
                                            }}
                                        />
                                    </td>
                                    <td>
                                        <input
                                            type = "number"
                                            min = "0"
                                            step = "0.01"
                                            className = "form-control"
                                            value = {line.quantity}
                                            onChange = {e => {
                                                const quantity = e.currentTarget.value;
                                                setLine(index, {
                                                    ...line,
                                                    quantity,
                                                    lineTotal: calcLineTotal(line.unitPrice, quantity),
                                                });
                                            }}
                                        />
                                    </td>
                                    <td>
                                        <input className = "form-control" readOnly value = {line.lineTotal} />
                                    </td>
                                    <td>
                                        <button
                                            type = "button"
                                            className = "btn btn-outline-danger"
                                            onClick = {() =>
                                                setPurchase(prev => ({
                                                    ...prev,
                                                    data: prev.data.length === 1
                                                        ? [{ ...defaultPurchaseLineItem }]
                                                        : prev.data.filter((_, lineIndex) => lineIndex !== index),
                                                }))
                                            }
                                        >
                                            Remove
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                <div className = "d-flex justify-content-end mt-3">
                    <div style = {{ minWidth: 320 }}>
                        <div className = "d-flex align-items-center mb-2">
                            <label className = "fw-bold me-3 text-nowrap" htmlFor = "tax">Tax</label>
                            <input
                                id = "tax"
                                name = "tax"
                                type = "number"
                                min = "0"
                                step = "0.01"
                                className = "form-control"
                                value = {purchase.tax ?? ""}
                                onChange = {e => setPurchase({ ...purchase, tax: e.currentTarget.value })}
                            />
                        </div>
                        <div className = "d-flex align-items-center mb-2">
                            <label className = "fw-bold me-3 text-nowrap" htmlFor = "discount">Discount</label>
                            <input
                                id = "discount"
                                name = "discount"
                                type = "number"
                                min = "0"
                                step = "0.01"
                                className = "form-control"
                                value = {purchase.discount ?? ""}
                                onChange = {e => setPurchase({ ...purchase, discount: e.currentTarget.value })}
                            />
                        </div>
                        <div className = "mb-2"><strong>Subtotal:</strong> ${subtotal.toFixed(2)}</div>
                        <div><strong>Total:</strong> ${total.toFixed(2)}</div>
                    </div>
                </div>
            </div>
            <div className = "my-3 px-3">
                <button type = "submit" className = "btn btn-primary">{keyword}</button>
                <Link to = ".." className = "btn btn-outline-secondary float-end">Back</Link>
            </div>
        </Form>
    );
}

function parseAmount(value?: string | null) {
    const parsed = Number(value || "0");
    return Number.isFinite(parsed) ? parsed : 0;
}

function calcLineTotal(unitPrice?: string, quantity?: string) {
    return round(parseAmount(unitPrice) * parseAmount(quantity), 2).toFixed(2);
}
